import { Metamodel } from '../base/model';
import { SharedModel as Base } from '../sharedmodel';
import { AdapterRegistry } from './adapter-registry';
import { NamedAdapter } from './named.adapter';
import { A365BackendModel } from '../a365.backendmodel';
import {
    Notebook, NbCell, NbLanguageInfo, NbKernelInfo, NbCodeCell, NbCodeCellOutputBase, NbCodeCellErrorOutput,
    NbCodeCellStreamOutput, NbCodeCellDisplayDataOutput, NbCodeCellExecuteResultOutput, NbMetadataA365Specific,
    NbComputeAuth, NbAuthor, NbCellMetadata, NbComputeOptions, NbComputeSessionProperties
} from '../notebook/notebook';
import { ModelConstants } from '../model-constants';
import { ReferenceAdapter } from './reference.adapter';
import { SparkConfiguration } from '../sparkconfiguration/sparkconfiguration';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(Notebook),
    adapts: {
        name: '*',
        properties: {
            description: '*',
            bigDataPool: '*',
            sessionProperties: '*',
            libraries: '*',
            metadata: '*',
            nbformat: '*',
            nbformat_minor: '*',
            cells: '*',
            folder: '*',
            targetSparkConfiguration: '*'
        }
    }
})
export class NotebookAdapter extends NamedAdapter<Notebook, A365BackendModel.NotebookResource> {
    protected _toResource(notebook: Notebook): A365BackendModel.NotebookResource {
        const notebookResource = super._toResource(notebook) as A365BackendModel.NotebookResource;

        if (notebook) {
            this.properties(notebookResource).nbformat =
                notebook.nbformat;

            notebookResource.properties.nbformat_minor =
                notebook.nbformatMinor;

            if (notebook.bigDataPool) {
                notebookResource.properties.bigDataPool = {
                    referenceName: notebook.bigDataPool,
                    type: 'BigDataPoolReference'
                };
            }

            if (notebook.targetSparkConfiguration) {
                notebookResource.properties.targetSparkConfiguration = this._getSparkConfigurationReference(notebook.targetSparkConfiguration);
            }

            if (notebook.sessionProperties) {
                const conf = notebook.sessionProperties.conf;
                notebookResource.properties.sessionProperties = {
                    driverMemory: notebook.sessionProperties.driverMemory,
                    driverCores: notebook.sessionProperties.driverCores,
                    executorMemory: notebook.sessionProperties.executorMemory,
                    executorCores: notebook.sessionProperties.executorCores,
                    numExecutors: notebook.sessionProperties.numExecutors,
                    runAsWorkspaceSystemIdentity: notebook.sessionProperties.runAsWorkspaceSystemIdentity,
                    conf: {
                        ...conf,
                        [A365BackendModel.keyForJobAutoScaleMin]: conf ? conf[A365BackendModel.keyForJobAutoScaleMin] : undefined,
                        [A365BackendModel.keyForJobAutoScaleMax]: conf ? conf[A365BackendModel.keyForJobAutoScaleMax] : undefined
                    }
                };
            }

            if (notebook.libraries) {
                notebookResource.properties.libraries =
                    [...notebook.libraries];
            }

            notebookResource.properties.metadata = {};
            if (notebook.metadata) {
                notebookResource.properties.metadata = {
                    ...(notebook.metadata.customMetadata || {})
                };

                if (notebook.metadata.kernelInfo) {
                    notebookResource.properties.metadata.kernelspec = {
                        name: notebook.metadata.kernelInfo.name,
                        display_name: notebook.metadata.kernelInfo.displayName
                    };
                }

                if (notebook.metadata.languageInfo) {
                    notebookResource.properties.metadata.language_info = {
                        name: notebook.metadata.languageInfo.name
                    };
                }

                if (notebook.metadata.authors) {
                    notebookResource.properties.metadata.authors =
                        (notebook.metadata.authors || []).map((author): A365BackendModel.Author => ({ name: author.name }));
                }

                if (notebook.metadata.computeOptions) {
                    notebookResource.properties.metadata.a365ComputeOptions =
                        this._convertToComputeOptionsResource(notebook.metadata);
                }

                if (notebook.metadata.sessionKeepAliveTimeout !== undefined) {
                    notebookResource.properties.metadata.sessionKeepAliveTimeout = notebook.metadata.sessionKeepAliveTimeout;
                }

                // for artifact --> ipython
                if (notebook.metadata.targetSparkConfiguration !== undefined) {
                    notebookResource.properties.metadata.targetSparkConfiguration = notebook.metadata.targetSparkConfiguration;
                }
            }

            if (notebook.cells) {
                notebookResource.properties.cells =
                    notebook.cells.map((cell) => {
                        return this._convertToCellResource(cell);
                    });
            }
        }

        return notebookResource;
    }

    protected _toModel(notebookResource: A365BackendModel.NotebookResource, notebook?: Notebook): Notebook {
        if (!notebook) {
            notebook = Notebook.MODEL.create<Notebook>();
        }
        super._toModel(notebookResource, notebook);
        notebook.resourceType = ModelConstants.notebook.toLowerCase() + 's';

        if (notebookResource.properties) {
            notebook.nbformat = notebookResource.properties.nbformat;
            notebook.nbformatMinor = notebookResource.properties.nbformat_minor;
            notebook.description = notebookResource.properties.description;
            notebook.cells = notebookResource.properties.cells
                ? notebookResource.properties.cells.map((cell) => this._convertToCellModel(cell))
                : undefined;

            if (notebookResource.properties.bigDataPool) {
                notebook.bigDataPool = notebookResource.properties.bigDataPool.referenceName;
            }

            if (notebookResource.properties.targetSparkConfiguration) {
                notebook.targetSparkConfiguration = ReferenceAdapter.toProxyModel<SparkConfiguration>(
                    Metamodel.modelFor(SparkConfiguration),
                    notebookResource.properties.targetSparkConfiguration, this.context);
            }

            if (notebookResource.properties.sessionProperties) {
                const { driverMemory, driverCores, executorMemory, executorCores, numExecutors, conf, runAsWorkspaceSystemIdentity } =
                    notebookResource.properties.sessionProperties;
                notebook.sessionProperties = new NbComputeSessionProperties();
                notebook.sessionProperties.driverMemory = driverMemory;
                notebook.sessionProperties.driverCores = driverCores;
                notebook.sessionProperties.executorMemory = executorMemory;
                notebook.sessionProperties.executorCores = executorCores;
                notebook.sessionProperties.numExecutors = numExecutors;
                notebook.sessionProperties.conf = conf;
                notebook.sessionProperties.runAsWorkspaceSystemIdentity = runAsWorkspaceSystemIdentity;
            }

            if (notebookResource.properties.libraries) {
                notebook.libraries = [...notebookResource.properties.libraries];
            }

            if (notebookResource.properties.metadata) {
                notebook.metadata = new NbMetadataA365Specific();

                const authorList = notebookResource.properties.metadata.authors;
                notebook.metadata.authors = authorList
                    ? authorList.map((author) => {
                        const authorModel = new NbAuthor();
                        authorModel.name = author.name;
                        return authorModel;
                    })
                    : undefined;

                if (notebookResource.properties.metadata.kernelspec) {
                    notebook.metadata.kernelInfo = new NbKernelInfo();
                    notebook.metadata.kernelInfo.name = notebookResource.properties.metadata.kernelspec.name;
                    notebook.metadata.kernelInfo.displayName = notebookResource.properties.metadata.kernelspec.display_name;
                }

                if (notebookResource.properties.metadata.language_info) {
                    notebook.metadata.languageInfo = new NbLanguageInfo();
                    notebook.metadata.languageInfo.name = notebookResource.properties.metadata.language_info.name;
                    notebook.metadata.languageInfo.version = notebookResource.properties.metadata.language_info.version;
                    notebook.metadata.languageInfo.codemirrorMode = notebookResource.properties.metadata.language_info.codemirror_mode;
                }

                if (notebookResource.properties.metadata.a365ComputeOptions) {
                    notebook.metadata.computeOptions = this._convertToComputeOptionsModel(
                        notebookResource.properties.metadata.a365ComputeOptions);
                }

                if (notebookResource.properties.metadata.sessionOptions) {
                    notebook.metadata.sessionOptions = { ...notebookResource.properties.metadata.sessionOptions };
                }

                if (notebookResource.properties.metadata.targetSparkConfiguration) {
                    notebook.metadata.targetSparkConfiguration = notebookResource.properties.metadata.targetSparkConfiguration;
                }

                if (notebookResource.properties.metadata.sessionKeepAliveTimeout) {
                    notebook.metadata.sessionKeepAliveTimeout = notebookResource.properties.metadata.sessionKeepAliveTimeout;
                }

                const predefinedKeys = ['authors', 'kernelspec', 'language_info', 'a365ComputeOptions', 'sessionOptions', 'sessionKeepAliveTimeout', 'targetSparkConfiguration'];
                const customMetadata = { ...notebookResource.properties.metadata };
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                Object.keys(customMetadata).filter((key) => predefinedKeys.includes(key)).forEach((key) => delete (customMetadata as any)[key]);
                notebook.metadata.customMetadata = customMetadata;
            }
        }
        return notebook;
    }

    private _convertToCellResource(cell: NbCell): A365BackendModel.NotebookCell {
        let metadata: A365BackendModel.NotebookCellMetadata;

        if (cell.metadata) {
            metadata = {
                ...(cell.metadata.customMetadata || {}),
                collapsed: cell.metadata.collapsed,
                scrolled: cell.metadata.scrolled,
                deletalble: cell.metadata.deletable,
                format: cell.metadata.format,
                name: cell.metadata.name,
                tags: cell.metadata.tags ? [...cell.metadata.tags] : undefined
            };
        }

        if (cell instanceof NbCodeCell) {
            return {
                cell_type: cell.cellType,
                metadata,
                source: (Array.isArray(cell)) ? [...cell.source] : cell.source,
                attachments: cell.attachments,
                outputs: (cell.outputs || []).map(this._convertToCellOutputResource),
                execution_count: cell.executionCount
            };
        }

        return {
            cell_type: cell.cellType as A365BackendModel.NotebookCellType,
            metadata,
            source: cell.source,
            attachments: cell.attachments
        };
    }

    private _convertToCellModel(cell: A365BackendModel.NotebookCell): NbCell {
        let cellModel: NbCell;
        if (cell.cell_type === 'code') {
            const codeCell = cell as A365BackendModel.NotebookCodeCell;
            const codeCellModel = new NbCodeCell();
            cellModel = codeCellModel;

            codeCellModel.executionCount = codeCell.execution_count;
            codeCellModel.outputs = (codeCell.outputs || []).map((output) => this._convertToCellOutputModel(output));
        } else {
            cellModel = new NbCell();
        }

        cellModel.attachments = cell.attachments;
        cellModel.cellType = cell.cell_type;
        cellModel.source = (Array.isArray(cell.source)) ? [...cell.source] : cell.source;

        if (cell.metadata) {
            cellModel.metadata = new NbCellMetadata();

            // Copy the metadata. Properties will be deleted from this copy as they are copied to
            // the model object in order to have only the custom metadata values at the end.
            const metadataCopy = { ...cell.metadata };
            cellModel.metadata.collapsed = metadataCopy.collapsed;
            delete metadataCopy.collapsed;
            cellModel.metadata.deletable = metadataCopy.deletable;
            delete metadataCopy.deletable;
            cellModel.metadata.format = metadataCopy.format;
            delete metadataCopy.format;
            cellModel.metadata.name = metadataCopy.name;
            delete metadataCopy.name;
            cellModel.metadata.scrolled = metadataCopy.scrolled;
            delete metadataCopy.scrolled;
            cellModel.metadata.tags = metadataCopy.tags ? [...metadataCopy.tags] : undefined;
            delete metadataCopy.tags;

            // Copy left over metadata values.
            cellModel.metadata.customMetadata = { ...metadataCopy };
        }
        return cellModel;
    }

    private _convertToCellOutputResource(cellOutput: NbCodeCellOutputBase): A365BackendModel.NotebookCodeCellOutput {

        if (cellOutput instanceof NbCodeCellStreamOutput) {
            const cellOutputModel: A365BackendModel.NotebookCodeCellStreamOutput = {
                name: cellOutput.name,
                output_type: cellOutput.outputType,
                text: cellOutput.text
            };
            return cellOutputModel;
        } else if (cellOutput instanceof NbCodeCellDisplayDataOutput) {
            const cellOutputModel: A365BackendModel.NotebookCodeCellDisplayDataOutput = {
                output_type: cellOutput.outputType,
                data: cellOutput.data,
                metadata: cellOutput.metadata
            };
            return cellOutputModel;
        } else if (cellOutput instanceof NbCodeCellExecuteResultOutput) {
            const cellOutputModel: A365BackendModel.NotebookCodeCellExecuteResultOutput = {
                execution_count: cellOutput.executionCount,
                output_type: cellOutput.outputType,
                data: cellOutput.data,
                metadata: cellOutput.metadata
            };
            return cellOutputModel;
        } else if (cellOutput instanceof NbCodeCellErrorOutput) {
            const cellOutputModel: A365BackendModel.NotebookCodeCellErrorOutput = {
                output_type: cellOutput.outputType,
                ename: cellOutput.ename,
                evalue: cellOutput.evalue,
                traceback: [...(cellOutput.traceback || [])]
            };
            return cellOutputModel;
        }
        return undefined;
    }

    private _convertToCellOutputModel(cellOutput: A365BackendModel.NotebookCodeCellOutput): NbCodeCellOutputBase {
        let cellOutputModel: NbCodeCellOutputBase;
        switch (cellOutput.output_type) {
            case 'stream':
                const cellStreamOutputModel = new NbCodeCellStreamOutput();
                cellOutputModel = cellStreamOutputModel;
                cellStreamOutputModel.name = cellOutput.name;
                cellStreamOutputModel.text = cellOutput.text;
                break;
            case 'display_data':
                const cellDisplayOutputModel = new NbCodeCellDisplayDataOutput();
                cellOutputModel = cellDisplayOutputModel;
                cellDisplayOutputModel.data = cellOutput.data;
                cellDisplayOutputModel.metadata = cellOutput.metadata;
                break;
            case 'error':
                const cellErrorOutputModel = new NbCodeCellErrorOutput();
                cellOutputModel = cellErrorOutputModel;
                cellErrorOutputModel.ename = cellOutput.ename;
                cellErrorOutputModel.evalue = cellOutput.evalue;
                cellErrorOutputModel.traceback = [...(cellOutput.traceback || [])];
                break;
            case 'execute_result':
                const executeOutputModel = new NbCodeCellExecuteResultOutput();
                cellOutputModel = executeOutputModel;
                executeOutputModel.data = cellOutput.data;
                executeOutputModel.executionCount = cellOutput.execution_count;
                executeOutputModel.metadata = cellOutput.metadata;
                break;
            default:
                if (typeof cellOutput !== 'object') {
                    return new NbCodeCellDisplayDataOutput();
                }
                break;
        }

        cellOutputModel.outputType = cellOutput.output_type;

        return cellOutputModel;
    }

    private _convertToComputeOptionsResource(metadata: NbMetadataA365Specific): A365BackendModel.ComputeOptions {
        if (metadata.computeOptions) {
            const computeOptionsModel: A365BackendModel.ComputeOptions = {
                id: metadata.computeOptions.id,
                name: metadata.computeOptions.name,
                type: metadata.computeOptions.computeType,
                endpoint: metadata.computeOptions.endpoint,
                auth: this._convertToComputeAuthResource(metadata.computeOptions.auth),
                sparkVersion: metadata.computeOptions.sparkVersion,
                nodeCount: metadata.computeOptions.nodeCount,
                cores: metadata.computeOptions.cores,
                memory: metadata.computeOptions.memory,
                extraHeader: metadata.computeOptions.extraHeader,
                extraParams: metadata.computeOptions.extraParams,
                automaticScaleJobs: metadata.computeOptions.automaticScaleJobs
            };
            return computeOptionsModel;
        }
        return undefined;
    }

    private _convertToComputeAuthResource(auth: NbComputeAuth): A365BackendModel.ComputeAuth {
        return {
            type: auth.computeAuthType as A365BackendModel.ComputeAuthType,
            authResource: auth.authResource,
            authHeader: auth.authHeader
        };
    }

    private _convertToComputeOptionsModel(computeResource: A365BackendModel.ComputeOptions): NbComputeOptions {
        const computeOptions = new NbComputeOptions();
        computeOptions.name = computeResource.name;
        computeOptions.id = computeResource.id;
        computeOptions.extraParams = computeResource.extraParams;
        computeOptions.extraHeader = computeResource.extraHeader;
        computeOptions.endpoint = computeResource.endpoint;
        computeOptions.computeType = computeResource.type;
        computeOptions.sparkVersion = computeResource.sparkVersion;
        computeOptions.nodeCount = computeResource.nodeCount;
        computeOptions.cores = computeResource.cores;
        computeOptions.memory = computeResource.memory;

        computeOptions.auth = new NbComputeAuth();
        computeOptions.auth.authHeader = computeResource.auth.authHeader;
        computeOptions.auth.authResource = computeResource.auth.authResource;
        computeOptions.auth.computeAuthType = computeResource.auth.type;
        return computeOptions;
    }

    private _getSparkConfigurationReference(named: SparkConfiguration): Base.ArtifactReference {
        if (!named) {
            return undefined;
        }
        return ReferenceAdapter.toResource(named, this.context) as Base.ArtifactReference;
    }

}
